import { JSONPath } from "jsonpath-plus";
import compact from "lodash/compact.js";
import { never } from "../../helpers/customError.js";
import { unreachable } from "../../helpers/type.js";
import { normalizePath, normalizeSnakePath } from "./pathHelpers.js";
import { QueryLinkValue } from "./queryLinkValue.js";
//
// Model.
//
export var QueryLinkType;
(function (QueryLinkType) {
    QueryLinkType["LINK"] = "link";
    QueryLinkType["PATH_LINK"] = "path_link";
    QueryLinkType["EMPTY_PATH_LINK"] = "empty_path_link";
})(QueryLinkType || (QueryLinkType = {}));
//
// I/O.
//
function buildLink(value) {
    return {
        type: QueryLinkType.LINK,
        value,
    };
}
function buildPathLink(path, value) {
    return {
        type: QueryLinkType.PATH_LINK,
        path: normalizePath(path),
        snakePath: normalizeSnakePath(path),
        value,
    };
}
function buildEmptyPathLink(path) {
    return {
        type: QueryLinkType.EMPTY_PATH_LINK,
        path: normalizePath(path),
        snakePath: normalizeSnakePath(path),
    };
}
const linkRegexp = /^(?:(\$[^=]+)=)?(.*)$/;
function queryLinkOfString(linkString) {
    const linkMatch = linkString.match(linkRegexp);
    if (!linkMatch || typeof linkMatch[2] !== "string") {
        return never();
    }
    const path = linkMatch[1];
    const valueString = linkMatch[2];
    // Empty path link.
    if (path && valueString === "") {
        return buildEmptyPathLink(path);
    }
    // Path link.
    const value = QueryLinkValue.ofString(valueString);
    if (path) {
        return buildPathLink(path, value);
    }
    // Link.
    return buildLink(value);
}
function queryLinkToString(queryLink) {
    switch (queryLink.type) {
        case QueryLinkType.LINK:
            return QueryLinkValue.toString(queryLink.value);
        case QueryLinkType.PATH_LINK: {
            const { snakePath } = queryLink;
            const valueString = QueryLinkValue.toString(queryLink.value);
            return `${snakePath}=${valueString}`;
        }
        case QueryLinkType.EMPTY_PATH_LINK:
            return `${queryLink.snakePath}=`;
        default:
            unreachable(queryLink[0]);
    }
}
// TODO: Implement link detection for accurate comparison.
function recordHasLinkAtPath(path, value, record) {
    const recordValues = JSONPath({ path, json: record });
    return recordValues.some(v => QueryLinkValue.is(value, v));
}
// TODO: Implement link detection for accurate comparison.
function recordHasEmptyPathLink(path, record) {
    const recordValues = JSONPath({ path, json: record });
    return compact(recordValues).length === 0;
}
function linkIsInRecord(record, queryLink) {
    switch (queryLink.type) {
        case QueryLinkType.LINK:
            throw new Error("TODO: Implement local link detection.");
        case QueryLinkType.PATH_LINK:
            return recordHasLinkAtPath(queryLink.path, queryLink.value, record);
        case QueryLinkType.EMPTY_PATH_LINK:
            return recordHasEmptyPathLink(queryLink.path, record);
        default:
            unreachable(queryLink[0]);
    }
}
function queryLinkIsSuperset(link1, link2) {
    switch (link1.type) {
        case QueryLinkType.LINK:
            return ((link2.type === QueryLinkType.LINK ||
                link2.type === QueryLinkType.PATH_LINK) &&
                QueryLinkValue.match(link1.value, link2.value));
        case QueryLinkType.PATH_LINK:
            return (link2.type === QueryLinkType.PATH_LINK &&
                link1.path === link2.path &&
                QueryLinkValue.match(link1.value, link2.value));
        case QueryLinkType.EMPTY_PATH_LINK:
            return (link2.type === QueryLinkType.EMPTY_PATH_LINK &&
                link1.path === link2.path);
        default:
            unreachable(link1[0]);
    }
}
export const QueryLink = {
    link: buildLink,
    pathLink: buildPathLink,
    emptyPathLink: buildEmptyPathLink,
    ofString: queryLinkOfString,
    toString: queryLinkToString,
    isInRecord: linkIsInRecord,
    isSuperset: queryLinkIsSuperset,
};
